import { Button, Dialog, Field, Notice } from '@concord/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import s from './pools.module.css';

export interface PoolSummary {
  id: string;
  name: string;
  description: string | null;
  caseTypes: { id: string; term: string }[];
  specialty: { id: string; term: string } | null;
  active: boolean;
  members: number;
}

export const POOLS_KEY = ['pools'] as const;

/** New pool, or edit one: name, description, the cancer types it serves, an optional specialty. */
export function PoolEditor({
  open,
  onOpenChange,
  pool,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  pool?: PoolSummary;
  onSaved?: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [types, setTypes] = useState<{ id: string; term: string }[]>([]);
  const [specialtyId, setSpecialtyId] = useState('');
  const [find, setFind] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(pool?.name ?? '');
    setDescription(pool?.description ?? '');
    setTypes(pool?.caseTypes ?? []);
    setSpecialtyId(pool?.specialty?.id ?? '');
    setFind('');
  }, [open, pool]);

  const specialties = useQuery({
    queryKey: ['specialties'],
    queryFn: () => api<{ items: { id: string; term: string }[] }>('/catalog/specialties'),
    enabled: open,
  });
  const matches = useQuery({
    queryKey: ['case-types', find],
    queryFn: () =>
      api<{ items: { id: string; term: string }[] }>(`/catalog/case-types?q=${encodeURIComponent(find)}`),
    enabled: open && find.trim().length > 0,
  });

  const save = useMutation({
    mutationFn: () => {
      const v = {
        name,
        description: description.trim() || null,
        caseTypeIds: types.map((t) => t.id),
        specialtyId: specialtyId || null,
      };
      return pool
        ? api<{ ok: true }>(`/staff/pools/${pool.id}`, {
            method: 'PATCH',
            body: { ...v, active: pool.active },
          }).then(() => ({ id: pool.id }))
        : api<{ id: string }>('/staff/pools', { body: v });
    },
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: POOLS_KEY });
      onOpenChange(false);
      onSaved?.(r.id);
    },
  });

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (name.trim().length >= 2 && !save.isPending) save.mutate();
  };
  const picked = new Set(types.map((t) => t.id));

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={pool ? 'Edit pool' : 'New physician pool'}
      description="A group of physicians for one or more cancer types. Navigators choose from it when building a panel."
      footer={
        <>
          <Button variant="sec" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button busy={save.isPending} disabled={name.trim().length < 2} onClick={() => submit()}>
            {pool ? 'Save changes' : 'Create pool'}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className={s.form}>
        <Field label="Pool name" hint="For example, GI oncology or Breast cancer: academic centers.">
          {(id, d) => (
            <input
              id={id}
              aria-describedby={d}
              className="input"
              maxLength={80}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        <Field label="Description" hint="Optional. Who is in it and when to use it.">
          {(id, d) => (
            <textarea
              id={id}
              aria-describedby={d}
              className="input"
              rows={2}
              maxLength={300}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </Field>
        <Field label="Cancer types" hint="The cases this pool serves. It is suggested first on those cases.">
          {(id, d) => (
            <div className={s.types}>
              {types.length > 0 && (
                <ul className={s.picked} aria-label="Chosen cancer types">
                  {types.map((t) => (
                    <li key={t.id}>
                      {t.term}
                      <button
                        type="button"
                        aria-label={`Remove ${t.term}`}
                        onClick={() => setTypes((x) => x.filter((y) => y.id !== t.id))}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <input
                id={id}
                aria-describedby={d}
                className="input"
                placeholder="Type to find a cancer type"
                value={find}
                onChange={(e) => setFind(e.target.value)}
              />
              {find.trim() && (
                <ul className={s.found} aria-label="Matching cancer types">
                  {matches.data?.items
                    .filter((m) => !picked.has(m.id))
                    .map((m) => (
                      <li key={m.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setTypes((x) => [...x, { id: m.id, term: m.term }]);
                            setFind('');
                          }}
                        >
                          + {m.term}
                        </button>
                      </li>
                    ))}
                  {matches.data && matches.data.items.filter((m) => !picked.has(m.id)).length === 0 && (
                    <li className="sub2">No other cancer type matches.</li>
                  )}
                </ul>
              )}
            </div>
          )}
        </Field>
        <Field label="Specialty" hint="Optional.">
          {(id, d) => (
            <select
              id={id}
              aria-describedby={d}
              className="select"
              value={specialtyId}
              onChange={(e) => setSpecialtyId(e.target.value)}
            >
              <option value="">Any specialty</option>
              {specialties.data?.items.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.term}
                </option>
              ))}
            </select>
          )}
        </Field>
        {save.isError && <Notice tone="bad">{errorMessage(save.error)}</Notice>}
      </form>
    </Dialog>
  );
}
